import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useStreamAuth } from '../hooks/useStreamAuth';
import spotlightService from '../services/SpotlightService';
import Header from '../components/common/Header';
import LoadingSpinner from '../components/auth/LoadingSpinner';

const TransactionHistoryPage = () => {
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();
  const { isAuthenticated } = useStreamAuth();
  
  const [subscription, setSubscription] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('📋 TransactionHistoryPage: Fetching transactions...');
        const data = await spotlightService.getTransactionHistory();
        console.log('📋 TransactionHistoryPage: Transactions fetched:', data);
        
        setSubscription(data.subscription);
        setPurchases(data.transactional_purchases || []);
      } catch (err) {
        console.error('📋 TransactionHistoryPage: Error fetching transactions:', err);
        setError(err.message || 'Failed to load transaction history');
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [isAuthenticated]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
    });
  };

  // Get expiration text
  const getExpirationText = (purchase) => {
    if (purchase.never_expires) {
      return 'Lifetime';
    }
    if (purchase.rental_end) {
      return `Expires ${formatDate(purchase.rental_end)}`;
    }
    if (purchase.product?.duration) {
      const { number, unit } = purchase.product.duration;
      return `${number} ${unit}${number > 1 ? 's' : ''}`;
    }
    return 'N/A';
  };

  // Handle watch button click
  const handleWatch = (purchase) => {
    const channel = purchase.channels?.[0];
    if (channel?.slug) {
      navigate(`/watch/${channel.slug}`);
    }
  };

  // Handle view details (open PDP)
  const handleViewDetails = (purchase) => {
    const channel = purchase.channels?.[0];
    if (channel?.slug) {
      navigate(`/?channel=${channel.slug}`);
    }
  };

  // Authentication check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-page text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Sign in required</h2>
          <p className="text-white/70 mb-6">You need to sign in to view your transaction history</p>
          <button
            onClick={() => loginWithRedirect()}
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-page">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-page">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-white mb-2">Something went wrong</h3>
            <p className="text-white/70 mb-6">{error}</p>
            <button
              className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasPurchases = purchases.length > 0;
  const hasSubscription = !!subscription;
  const hasContent = hasPurchases || hasSubscription;

  return (
    <div className="min-h-screen bg-page text-white">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          
          <h1 className="text-3xl font-bold text-white mb-2">Transaction History</h1>
          <p className="text-white/70">View your purchase and subscription history</p>
        </div>

        {/* No Content State */}
        {!hasContent && (
          <div className="text-center py-12 bg-gray-800/30 rounded-lg">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-white mb-2">No transactions yet</h3>
            <p className="text-white/70 mb-6">
              Your purchase and subscription history will appear here
            </p>
            <button
              className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
              onClick={() => navigate('/')}
            >
              Browse Content
            </button>
          </div>
        )}

        {/* Active Subscription Section */}
        {hasSubscription && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Active Subscription
            </h2>
            <div className="bg-gradient-to-r from-green-500/10 to-transparent border border-green-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">{subscription.product?.name || 'Subscription'}</h3>
                  <p className="text-white/70 text-sm mt-1">{subscription.product?.description || 'Full access to content'}</p>
                </div>
                <span className="px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full text-sm font-medium">
                  Active
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Purchases Section */}
        {hasPurchases && (
          <div>
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Purchases ({purchases.length})
            </h2>

            <div className="space-y-4">
              {purchases.map((purchase, index) => {
                const channel = purchase.channels?.[0];
                const posterUrl = channel?.poster || channel?.spotlight_poster || null;
                const productName = purchase.product?.name || channel?.title || 'Unknown Product';
                const price = purchase.product?.price || 'N/A';
                const productType = purchase.product?.product_type?.toUpperCase() || 'PURCHASE';
                const adsEnabled = purchase.product?.ads_enabled;

                return (
                  <div
                    key={purchase._id || index}
                    className="bg-gray-800/30 rounded-lg overflow-hidden hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-stretch">
                      {/* Poster Image */}
                      <div className="w-24 sm:w-32 md:w-40 flex-shrink-0 bg-gray-900">
                        {posterUrl ? (
                          <img 
                            src={posterUrl} 
                            alt={productName}
                            className="w-full h-full object-cover"
                            style={{ aspectRatio: '2/3' }}
                          />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center bg-gray-700 text-gray-500"
                            style={{ aspectRatio: '2/3' }}
                          >
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Transaction Details */}
                      <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between">
                        <div>
                          {/* Title Row */}
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-white line-clamp-1">
                              {productName}
                            </h3>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Product Type Badge */}
                              <span className="px-2 py-0.5 bg-accent/20 text-accent border border-accent/30 rounded text-xs font-medium">
                                {productType}
                              </span>
                              {/* Ad-Free Badge */}
                              {!adsEnabled && (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded text-xs font-medium">
                                  Ad-Free
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Info Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm mb-3">
                            <div>
                              <span className="text-white/50">Purchased</span>
                              <p className="text-white">{formatDate(purchase.rental_start)}</p>
                            </div>
                            <div>
                              <span className="text-white/50">Price</span>
                              <p className="text-white font-medium">{price}</p>
                            </div>
                            <div>
                              <span className="text-white/50">Access</span>
                              <p className="text-white">{getExpirationText(purchase)}</p>
                            </div>
                          </div>

                          {/* Platform Badge */}
                          <div className="text-xs text-white/40">
                            Purchased via {purchase.platform || 'web'}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {channel?.slug && (
                          <div className="flex gap-3 mt-4">
                            <button
                              onClick={() => handleWatch(purchase)}
                              className="bg-accent hover:bg-accent/80 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                              Watch Now
                            </button>
                            <button
                              onClick={() => handleViewDetails(purchase)}
                              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                            >
                              View Details
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistoryPage;
